import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Banknote, ClipboardCheck, Scale, ArrowRight, HelpCircle } from "lucide-react";
import ComplianceStrip from "../components/ComplianceStrip";
import Seo from "../components/Seo";
import { track } from "../lib/api";

const PROCESS = [
  "Request your quote through MortgagePath",
  "Choose whether to proceed",
  "Complete the qualifying service",
  "Submit your cashback claim",
  "Receive your £50 cashback",
];

const FAQS = [
  {
    q: "What cashback is currently available?",
    a: "Two separate MortgagePath cashback rewards: £50 cashback after you complete a qualifying property survey through MortgagePath, and £50 cashback after you complete qualifying conveyancing through MortgagePath.",
  },
  {
    q: "Can I claim both?",
    a: "Yes. The two cashback rewards are assessed independently. If you complete both a qualifying survey and qualifying conveyancing through MortgagePath, you can qualify for both — up to £100 cashback in total — provided the requirements for each are met.",
  },
  {
    q: "When do I qualify for the £50 cashback?",
    a: "Only after the relevant qualifying service has been completed and paid for in full, and we can confirm it with the provider. Each cashback reward has its own eligibility requirements.",
  },
  {
    q: "Does requesting a quote qualify me?",
    a: "No. Requesting a quote is free and no-obligation, but it does not earn cashback on its own. The qualifying service must be completed.",
  },
  {
    q: "Who provides the survey?",
    a: "Surveys are carried out by our selected RICS-regulated survey provider. MortgagePath is not a surveyor and does not provide survey advice.",
  },
  {
    q: "Who provides the conveyancing?",
    a: "Conveyancing is carried out by our selected regulated conveyancing provider. MortgagePath is not a conveyancer and does not provide legal advice.",
  },
  {
    q: "How do I claim my cashback?",
    a: "Once your qualifying service completes, email info@mortgagepath.uk with your MortgagePath reference so we can verify your claim and arrange your cashback payment.",
  },
  {
    q: "How is the cashback paid?",
    a: "Cashback is paid by secure UK bank transfer once we have verified your completed service. We'll request your bank details through a secure channel at that stage — never through a website form or ordinary email.",
  },
  {
    q: "Does the cashback affect the service I receive?",
    a: "No. The £50 cashback is a separate MortgagePath promotion, funded and administered by MortgagePath. It does not affect the service, the price you are quoted, or any professional advice or recommendations from the provider.",
  },
  {
    q: "Does MortgagePath receive a referral fee?",
    a: "Where you request a survey or conveyancing quote through MortgagePath, we may receive a referral fee. This does not increase what you pay and never affects the educational information we provide.",
  },
];

const RewardCard = ({ icon: Icon, eyebrow, title, body, cta, to, onClick, testid }) => (
  <div className="flex flex-col rounded-2xl border border-[#DCE7E3] bg-white p-6 sm:p-7 shadow-sm" data-testid={testid}>
    <div className="flex items-center gap-3">
      <span className="w-11 h-11 rounded-xl bg-[#0F766E]/10 border border-[#0F766E]/20 grid place-items-center shrink-0" aria-hidden="true">
        <Icon className="w-5 h-5 text-[#0F766E]" />
      </span>
      <div className="text-[11px] uppercase tracking-widest text-[#0F766E]">{eyebrow}</div>
    </div>
    <h3 className="font-sora text-xl font-semibold text-[#0B1220] mt-4">{title}</h3>
    <p className="mt-2 text-sm text-[#475569] leading-relaxed flex-1">{body}</p>
    <Link to={to} onClick={onClick} className="mp-btn-primary mt-5 w-full justify-center" data-testid={`${testid}-cta`}>
      {cta} <ArrowRight className="w-4 h-4" />
    </Link>
  </div>
);

const Rewards = () => {
  useEffect(() => { track("rewards_page_view", {}); }, []);

  return (
    <div className="mp-container py-12 sm:py-16 max-w-5xl">
      <Seo
        title="MortgagePath Cashback — up to £100 on survey & conveyancing | MortgagePath"
        description="Arrange your property survey or conveyancing through MortgagePath and receive £50 cashback when each qualifying service completes — up to £100 in total. Terms apply."
        path="/rewards"
      />

      {/* Hero */}
      <section className="rounded-2xl border border-[#0F766E]/20 bg-gradient-to-br from-[#0F766E]/6 via-white to-[#10B981]/8 p-6 sm:p-10" data-testid="rewards-hero">
        <div className="text-xs uppercase tracking-widest text-[#0F766E] flex items-center gap-2">
          <Banknote className="w-3.5 h-3.5" /> Up to £100 cashback
        </div>
        <h1 className="font-sora mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B1220]">
          Get up to £100 cashback on your homebuying journey
        </h1>
        <p className="mt-4 text-[#475569] leading-relaxed max-w-2xl">
          Buying a property? Arrange your survey or conveyancing through MortgagePath and receive
          {" "}<strong className="text-[#0B5F58]">£50 cashback</strong> when your qualifying service completes.
          Use both eligible services to receive up to £100.
        </p>
      </section>

      {/* Two cashback cards */}
      <div className="mt-8 grid md:grid-cols-2 gap-5">
        <RewardCard
          icon={ClipboardCheck}
          eyebrow="Property Survey — £50 Cashback"
          title="£50 cashback on a completed survey"
          body="£50 cashback on an eligible completed property survey. You must arrange your qualifying survey through MortgagePath to qualify."
          cta="Get a survey quote"
          to="/survey-quote?source=rewards_hub"
          onClick={() => track("rewards_survey_click", {})}
          testid="rewards-survey-card"
        />
        <RewardCard
          icon={Scale}
          eyebrow="Conveyancing — £50 Cashback"
          title="£50 cashback on completed conveyancing"
          body="£50 cashback on eligible completed conveyancing. You must arrange your conveyancing through MortgagePath to qualify."
          cta="Get a conveyancing quote"
          to="/conveyancing-quote?source=rewards_hub"
          onClick={() => track("rewards_conveyancing_click", {})}
          testid="rewards-conveyancing-card"
        />
      </div>

      {/* Use one or both */}
      <section className="mt-8 rounded-2xl border border-[#DCE7E3] bg-[#F7FAF9] p-6 sm:p-7" data-testid="rewards-both">
        <h2 className="font-sora text-xl font-semibold text-[#0B1220]">Use one or both — your choice</h2>
        <p className="mt-2 text-sm text-[#475569] leading-relaxed max-w-3xl">
          Each cashback reward is available independently — you do not need to use both services to qualify for an
          individual £50 payment. If you complete both a qualifying survey and qualifying conveyancing through
          MortgagePath, you can qualify for both — <strong className="text-[#0B1220]">£100 cashback in total</strong> —
          subject to the terms of each. The two rewards are separate and assessed independently.
        </p>
      </section>

      {/* How it works */}
      <section className="mt-10" data-testid="rewards-process">
        <h2 className="font-sora text-2xl font-bold text-[#0B1220] tracking-tight">How it works</h2>
        <ol className="mt-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {PROCESS.map((label, i) => (
            <li key={label} className="rounded-xl border border-[#DCE7E3] bg-white p-4">
              <div className="w-8 h-8 rounded-full bg-[#0F766E]/10 text-[#0F766E] grid place-items-center font-sora font-semibold text-sm">{i + 1}</div>
              <p className="mt-3 text-sm text-[#0B1220] leading-snug">{label}</p>
            </li>
          ))}
        </ol>
        <p className="mt-4 text-sm text-[#475569] leading-relaxed">
          <strong className="text-[#0B1220]">Requesting a quote does not earn cashback.</strong> Cashback is only due
          after the qualifying service has been completed and the requirements have been met.
        </p>
      </section>

      {/* Claim + terms */}
      <section className="mt-10 rounded-2xl border border-[#0F766E]/20 bg-[#0F766E]/5 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5 justify-between" data-testid="rewards-claim">
        <div>
          <h2 className="font-sora text-xl font-semibold text-[#0B1220]">Completed a qualifying service?</h2>
          <p className="mt-2 text-sm text-[#475569] leading-relaxed max-w-xl">
            Email us your MortgagePath reference and we'll verify your claim and arrange your £50 cashback by secure
            bank transfer. You'll find the full conditions and how to claim in the cashback terms.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 shrink-0">
          <Link to="/contact" onClick={() => track("rewards_claim_click", {})} className="mp-btn-primary justify-center" data-testid="rewards-claim-cta">
            Claim my cashback <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/reward-terms" onClick={() => track("rewards_terms_click", {})} className="mp-btn-secondary justify-center" data-testid="rewards-terms-cta">
            Cashback terms
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="mt-12" data-testid="rewards-faq">
        <h2 className="font-sora text-2xl font-bold text-[#0B1220] tracking-tight">Cashback FAQs</h2>
        <div className="mt-5 space-y-3">
          {FAQS.map(({ q, a }) => (
            <details key={q} className="group rounded-xl border border-[#DCE7E3] bg-white p-4 open:shadow-sm">
              <summary className="flex items-start gap-3 cursor-pointer list-none">
                <HelpCircle className="w-5 h-5 text-[#0F766E] shrink-0 mt-0.5" aria-hidden="true" />
                <span className="font-sora text-base font-semibold text-[#0B1220]">{q}</span>
              </summary>
              <p className="mt-3 pl-8 text-sm text-[#475569] leading-relaxed">{a}</p>
            </details>
          ))}
        </div>
      </section>

      <div className="mt-10"><ComplianceStrip /></div>
    </div>
  );
};

export default Rewards;
